import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { SEGMENT_COLORS, SCORE_COLORS } from "../constants/segments";
import { formatCompactCurrency, formatCurrency, formatNumber, formatTitleCase } from "../utils/formatters";
import { getPaginationRange } from "../utils/dashboardHelpers";
import CustomerActionPanel from "./CustomerActionPanel";
import FilterMetricIcon from "./FilterMetricIcon";
import MetricRangeSlider from "./MetricRangeSlider";

function toDateValue(rawDate) {
  if (!rawDate) {
    return null;
  }

  const text = String(rawDate).trim();
  if (!text) {
    return null;
  }

  const ymd = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) {
    return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getDaysSince(rawDate) {
  const purchaseDate = toDateValue(rawDate);
  if (!purchaseDate) {
    return null;
  }

  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const purchaseStart = new Date(
    purchaseDate.getFullYear(),
    purchaseDate.getMonth(),
    purchaseDate.getDate()
  );
  const diff = Math.floor((todayStart - purchaseStart) / (1000 * 60 * 60 * 24));
  return diff >= 0 ? diff : 0;
}

function getDateStart(rawDate) {
  const date = toDateValue(rawDate);
  if (!date) {
    return null;
  }

  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function formatMonthYear(rawDate) {
  const date = toDateValue(rawDate);
  if (!date) {
    return "";
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "2-digit",
  });
}

function ScoreBadge({ score }) {
  const color = SCORE_COLORS[score] || "#8a92a3";
  return (
    <span className="score-badge" style={{ background: `${color}26`, color, borderColor: `${color}44` }}>
      {score}
    </span>
  );
}

const PAGE_SIZE_OPTIONS = [20, 25, 50, 100];

function getMetricRange(rows, key, options = {}) {
  const values = (rows || [])
    .map((row) => Number(row?.[key] || 0))
    .filter((value) => Number.isFinite(value) && (!options.positiveMin || value > 0));

  if (!values.length) {
    return { min: "", max: "" };
  }

  return {
    min: String(Math.floor(Math.min(...values))),
    max: String(Math.ceil(Math.max(...values))),
  };
}

export default function CustomerTable({
  customers,
  selectedSegments,
  onSelectedSegmentsChange,
  sortKey,
  sortDirection,
  onSort,
  selectedCustomerId,
  onSelectCustomer,
  selectedCustomer,
  timeline,
  isTimelineLoading,
  terminology = {},
}) {
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isSegmentMenuOpen, setIsSegmentMenuOpen] = useState(false);
  const [draftSegments, setDraftSegments] = useState([]);
  const [minVisits, setMinVisits] = useState("");
  const [maxVisits, setMaxVisits] = useState("");
  const [minMonetary, setMinMonetary] = useState("");
  const [maxMonetary, setMaxMonetary] = useState("");
  const [lastPurchaseAfter, setLastPurchaseAfter] = useState("");
  const segmentMenuRef = useRef(null);
  const lastPurchaseInputRef = useRef(null);
  const tableWrapRef = useRef(null);
  const activityPlural = terminology.activity_plural_label || "Activities";
  const activityCountLabel = terminology.activity_count_label || activityPlural;
  const lastActivityLabel = terminology.last_activity_label || "Last Activity";
  const totalSpendLabel = terminology.total_spend_label || "Total Spend";
  const spendLabel = terminology.spend_label || "Spend";
  const customerLabel = terminology.customer_label || "Customer";
  const customerPlural = terminology.customer_plural_label || "Customers";

  const columns = useMemo(
    () => [
      { key: "full_name", label: "Name" },
      { key: "phone_number", label: "Phone" },
      { key: "r_score", label: "R" },
      { key: "f_score", label: "F" },
      { key: "m_score", label: "M" },
      { key: "composite_score", label: "Score" },
      { key: "segment", label: "Segment" },
      { key: "last_purchase_date", label: lastActivityLabel },
      { key: "total_spend", label: totalSpendLabel },
      { key: "visit_count", label: activityCountLabel },
    ],
    [activityCountLabel, lastActivityLabel, totalSpendLabel]
  );

  const segmentOptions = useMemo(() => {
    const availableSegments = new Set(customers.map((item) => item.segment).filter(Boolean));
    const orderedSegments = Object.keys(SEGMENT_COLORS).filter((segment) => availableSegments.has(segment));
    const extraSegments = [...availableSegments].filter((segment) => !SEGMENT_COLORS[segment]);
    return orderedSegments.length || extraSegments.length
      ? [...orderedSegments, ...extraSegments]
      : Object.keys(SEGMENT_COLORS);
  }, [customers]);

  const selectedSegmentSet = useMemo(() => new Set(selectedSegments), [selectedSegments]);
  const draftSegmentSet = useMemo(() => new Set(draftSegments), [draftSegments]);

  const segmentFilteredRows = useMemo(
    () => customers.filter((row) => selectedSegmentSet.has(row.segment)),
    [customers, selectedSegmentSet]
  );

  const visitRange = useMemo(() => getMetricRange(segmentFilteredRows, "visit_count"), [segmentFilteredRows]);
  const monetaryRange = useMemo(() => getMetricRange(segmentFilteredRows, "total_spend"), [segmentFilteredRows]);

  useEffect(() => {
    setMinVisits(visitRange.min);
    setMaxVisits(visitRange.max);
    setMinMonetary(monetaryRange.min);
    setMaxMonetary(monetaryRange.max);
    setPage(1);
  }, [visitRange.min, visitRange.max, monetaryRange.min, monetaryRange.max]);

  useEffect(() => {
    if (isSegmentMenuOpen) {
      setDraftSegments(selectedSegments);
    }
  }, [isSegmentMenuOpen, selectedSegments]);

  const filteredRows = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    const minV = minVisits ? parseInt(minVisits, 10) : 0;
    const maxV = maxVisits ? parseInt(maxVisits, 10) : Infinity;
    const minM = minMonetary ? parseFloat(minMonetary) : 0;
    const maxM = maxMonetary ? parseFloat(maxMonetary) : Infinity;
    const lastPurchaseAfterDate = getDateStart(lastPurchaseAfter);

    return customers.filter((row) => {
      const matchesSegment = selectedSegmentSet.has(row.segment);
      const rowLastPurchaseDate = getDateStart(row.last_purchase_date);
      const matchesQuery =
        !query ||
        (row.full_name || row.first_name || "").toLowerCase().includes(query) ||
        (row.phone_number || "").toLowerCase().includes(query);
      const matchesVisits = row.visit_count >= minV && row.visit_count <= maxV;
      const matchesMonetary = row.total_spend >= minM && row.total_spend <= maxM;
      const matchesLastPurchase =
        !lastPurchaseAfterDate ||
        (rowLastPurchaseDate && rowLastPurchaseDate >= lastPurchaseAfterDate);

      return matchesSegment && matchesQuery && matchesVisits && matchesMonetary && matchesLastPurchase;
    });
  }, [
    customers,
    searchText,
    selectedSegmentSet,
    minVisits,
    maxVisits,
    minMonetary,
    maxMonetary,
    lastPurchaseAfter,
  ]);

  const sortedRows = useMemo(() => {
    const rows = [...filteredRows];
    rows.sort((leftRow, rightRow) => {
      const leftValue = leftRow?.[sortKey] ?? "";
      const rightValue = rightRow?.[sortKey] ?? "";

      if (typeof leftValue === "string") {
        return leftValue.localeCompare(rightValue) * sortDirection;
      }

      return (leftValue - rightValue) * sortDirection;
    });
    return rows;
  }, [filteredRows, sortDirection, sortKey]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);

  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [currentPage, sortedRows, pageSize]);

  useLayoutEffect(() => {
    if (tableWrapRef.current) {
      tableWrapRef.current.scrollLeft = 0;
    }
  }, [currentPage, pageSize, searchText, selectedSegments, sortDirection, sortKey, customers.length, lastPurchaseAfter]);

  const segmentLabel =
    selectedSegments.length === 0
      ? "No Segments"
      : selectedSegments.length === segmentOptions.length
      ? "All Segments"
      : `${selectedSegments.length} of ${segmentOptions.length} Segments`;

  const detailHint = selectedCustomerId
    ? `Selected ${customerLabel.toLowerCase()} details are expanded in-row: Last 5 ${activityPlural}, ${spendLabel} Trend, and Action Log.`
    : `Tip: Click a ${customerLabel.toLowerCase()} row to view Last 5 ${activityPlural}, ${spendLabel} Trend, and Action Log for that ${customerLabel.toLowerCase()}.`;

  useEffect(() => {
    function onOutsideClick(event) {
      if (segmentMenuRef.current && !segmentMenuRef.current.contains(event.target)) {
        setIsSegmentMenuOpen(false);
      }
    }

    if (isSegmentMenuOpen) {
      document.addEventListener("mousedown", onOutsideClick);
    }

    return () => document.removeEventListener("mousedown", onOutsideClick);
  }, [isSegmentMenuOpen]);

  function toggleDraftSegment(segment) {
    if (draftSegmentSet.has(segment)) {
      setDraftSegments(draftSegments.filter((item) => item !== segment));
    } else {
      setDraftSegments([...draftSegments, segment]);
    }
  }

  function applyDraftSegments() {
    onSelectedSegmentsChange(draftSegments);
    setPage(1);
    setIsSegmentMenuOpen(false);
  }

  function resetFilters() {
    setMinVisits(visitRange.min);
    setMaxVisits(visitRange.max);
    setMinMonetary(monetaryRange.min);
    setMaxMonetary(monetaryRange.max);
    setLastPurchaseAfter("");
    setPage(1);
  }

  const hasActiveFilters =
    minVisits !== visitRange.min ||
    maxVisits !== visitRange.max ||
    minMonetary !== monetaryRange.min ||
    maxMonetary !== monetaryRange.max ||
    Boolean(lastPurchaseAfter);

  function handleVisitsInput(value) {
    const numValue = value.replace(/[^0-9]/g, "");
    setMinVisits(numValue);
    setPage(1);
  }

  function handleVisitsMaxInput(value) {
    const numValue = value.replace(/[^0-9]/g, "");
    setMaxVisits(numValue);
    setPage(1);
  }

  function handleMonetaryInput(value) {
    const numValue = value.replace(/[^0-9.]/g, "");
    setMinMonetary(numValue);
    setPage(1);
  }

  function handleMonetaryMaxInput(value) {
    const numValue = value.replace(/[^0-9.]/g, "");
    setMaxMonetary(numValue);
    setPage(1);
  }

  function handleLastPurchaseAfterInput(value) {
    setLastPurchaseAfter(value);
    setPage(1);
  }

  function openLastPurchasePicker() {
    const input = lastPurchaseInputRef.current;
    if (!input) {
      return;
    }

    input.focus();
    if (typeof input.showPicker === "function") {
      try {
        input.showPicker();
      } catch (_error) {
        // Some browsers only allow showPicker during direct user gestures.
      }
    }
  }

  function renderLastPurchaseCell(lastPurchaseDate) {
    if (!lastPurchaseDate) {
      return "-";
    }

    const daysSince = getDaysSince(lastPurchaseDate);
    return (
      <span className="last-purchase-wrap">
        <span className="last-purchase-date">{lastPurchaseDate}</span>
        {daysSince === null ? null : (
          <span className="last-purchase-age">({daysSince} days ago)</span>
        )}
      </span>
    );
  }

  return (
    <section className="card customer-table-card">
      <div className="table-header">
        <div className="card-label">Customer Segments</div>
      </div>

      <div className="compact-filters-row" ref={segmentMenuRef}>
        <div className="filter-left-section">
          <button
            type="button"
            className="segment-select-trigger"
            onClick={() => setIsSegmentMenuOpen((current) => !current)}
          >
            <span className={`segment-filter-label ${selectedSegments.length !== segmentOptions.length ? "active" : ""}`}>
              {formatTitleCase(segmentLabel)}
            </span>
            <span className="segment-select-caret">{isSegmentMenuOpen ? "▴" : "▾"}</span>
          </button>

          {isSegmentMenuOpen ? (
            <div className="segment-select-menu">
              <div className="segment-select-actions">
                <button
                  type="button"
                  className="segment-filter-btn"
                  onClick={() => {
                    setDraftSegments(segmentOptions);
                  }}
                >
                  All
                </button>
                <button
                  type="button"
                  className="segment-filter-btn"
                  onClick={() => {
                    setDraftSegments([]);
                  }}
                >
                  None
                </button>
              </div>

              <div className="segment-select-options">
                {segmentOptions.map((segment) => {
                  const color = SEGMENT_COLORS[segment] || "#8a92a3";
                  return (
                    <label key={segment} className="segment-check-item">
                      <input
                        type="checkbox"
                        checked={draftSegmentSet.has(segment)}
                        onChange={() => toggleDraftSegment(segment)}
                      />
                      <span className="segment-dot" style={{ background: color }} />
                      <span>{formatTitleCase(segment)}</span>
                    </label>
                  );
                })}
              </div>

              <div className="segment-select-footer">
                <button
                  type="button"
                  className="segment-filter-btn"
                  onClick={() => setIsSegmentMenuOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="segment-filter-apply"
                  onClick={applyDraftSegments}
                >
                  Apply
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="filter-date-section">
          <label className="compact-filter-label">
            <FilterMetricIcon type="calendar" />
            <span>Last Purchase After</span>
          </label>
          <div
            className={`date-filter-control${lastPurchaseAfter ? " active" : ""}`}
            onClick={openLastPurchasePicker}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openLastPurchasePicker();
              }
            }}
            role="button"
            tabIndex={0}
          >
            <input
              ref={lastPurchaseInputRef}
              type="date"
              className="date-filter-input"
              value={lastPurchaseAfter}
              onChange={(event) => handleLastPurchaseAfterInput(event.target.value)}
              aria-label="Last Purchase After"
            />
            <span className="date-filter-display">
              {lastPurchaseAfter ? formatMonthYear(lastPurchaseAfter) : "Date"}
            </span>
            {lastPurchaseAfter ? (
              <button
                type="button"
                className="date-filter-clear"
                onClick={(event) => {
                  event.stopPropagation();
                  handleLastPurchaseAfterInput("");
                }}
                aria-label="Clear Last Purchase After filter"
              >
                ×
              </button>
            ) : null}
          </div>
        </div>

        <div className="filter-right-section">
          <div className="filter-bottom-row">
            <div className="compact-filter-group">
              <label className="compact-filter-label"><FilterMetricIcon type="visits" /><span>{activityCountLabel}</span></label>
              <MetricRangeSlider
                minValue={minVisits}
                maxValue={maxVisits}
                rangeMin={visitRange.min}
                rangeMax={visitRange.max}
                step={1}
                onMinChange={handleVisitsInput}
                onMaxChange={handleVisitsMaxInput}
                formatValue={formatNumber}
              />
              <div className="compact-filter-inputs">
                <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    step="1"
                    className="compact-filter-input"
                    placeholder="Min"
                    value={minVisits}
                    onChange={(e) => handleVisitsInput(e.target.value)}
                />
                <span className="compact-filter-separator">–</span>
                <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    step="1"
                    className="compact-filter-input"
                    placeholder="Max"
                    value={maxVisits}
                    onChange={(e) => handleVisitsMaxInput(e.target.value)}
                />
              </div>
            </div>

            <div className="compact-filter-group">
              <label className="compact-filter-label"><FilterMetricIcon type="monetary" /><span>{spendLabel}</span></label>
              <MetricRangeSlider
                minValue={minMonetary}
                maxValue={maxMonetary}
                rangeMin={monetaryRange.min}
                rangeMax={monetaryRange.max}
                step={1}
                onMinChange={handleMonetaryInput}
                onMaxChange={handleMonetaryMaxInput}
                formatValue={formatCompactCurrency}
              />
              <div className="compact-filter-inputs">
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="1"
                  className="compact-filter-input"
                  placeholder="Min"
                  value={minMonetary}
                  onChange={(e) => handleMonetaryInput(e.target.value)}
                />
                <span className="compact-filter-separator">–</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="1"
                  className="compact-filter-input"
                  placeholder="Max"
                  value={maxMonetary}
                  onChange={(e) => handleMonetaryMaxInput(e.target.value)}
                />
              </div>
            </div>

            {false && hasActiveFilters && (
              <button
                type="button"
                className="compact-filter-reset"
                onClick={resetFilters}
                title="Clear filters"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="filter-search-row customer-segment-header-search">
          <input
            type="text"
            className="search-input customer-table-search-input"
            placeholder={`Search by ${customerLabel.toLowerCase()} name or phone...`}
            value={searchText}
            onChange={(event) => {
              setSearchText(event.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <div className="customer-table-helper">{detailHint}</div>

      {selectedCustomerId && (
        <div className="customer-banner">
          <span>Dashboard charts now show the selected customer view.</span>
          <button type="button" onClick={() => onSelectCustomer(null)}>
            Clear
          </button>
        </div>
      )}

      <div className="table-wrap" ref={tableWrapRef}>
        <table className="rfm-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} onClick={() => onSort(column.key)}>
                  {column.label}
                  {sortKey === column.key ? (sortDirection === -1 ? " ↓" : " ↑") : ""}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {!pageRows.length ? (
              <tr>
                <td colSpan={columns.length} className="empty-cell">
                  No {customerPlural.toLowerCase()} match current filters.
                </td>
              </tr>
            ) : (
              pageRows.map((row) => {
                const segmentColor = SEGMENT_COLORS[row.segment] || "#8a92a3";
                const isSelected = row.customer_id === selectedCustomerId;
                return (
                  <React.Fragment key={row.customer_id}>
                    <tr
                      className={isSelected ? "selected" : ""}
                      onClick={() =>
                        onSelectCustomer(isSelected ? null : row.customer_id)
                      }
                    >
                      <td className="name-col">{formatTitleCase(row.full_name || row.first_name || "-").toUpperCase()}</td>
                      <td className="mono-text">{row.phone_number || "-"}</td>
                      <td>
                        <ScoreBadge score={row.r_score} />
                      </td>
                      <td>
                        <ScoreBadge score={row.f_score} />
                      </td>
                      <td>
                        <ScoreBadge score={row.m_score} />
                      </td>
                      <td className="mono-text bold">{row.composite_score}</td>
                      <td>
                        <span
                          className="segment-badge"
                          style={{
                            color: segmentColor,
                            borderColor: `${segmentColor}66`,
                            background: `${segmentColor}1a`,
                          }}
                        >
                          {formatTitleCase(row.segment)}
                        </span>
                      </td>
                      <td className="mono-text">{renderLastPurchaseCell(row.last_purchase_date)}</td>
                      <td className="mono-text">{formatCurrency(row.total_spend)}</td>
                      <td className="mono-text">{row.visit_count}</td>
                    </tr>
                    {isSelected && (
                      <tr className="customer-inline-row">
                        <td colSpan={columns.length} className="customer-inline-cell">
                          <CustomerActionPanel
                            customer={selectedCustomer}
                            timeline={timeline}
                            isLoading={isTimelineLoading}
                            inline
                            terminology={terminology}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="table-pagination">
        <div className="pagination-size-control-right">
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(1);
            }}
            className="size-dropdown"
            title="Select rows per page"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size} rows
              </option>
            ))}
          </select>
        </div>
        <div className="page-btn-wrap">
          <button
            type="button"
            className="page-btn"
            disabled={currentPage <= 1}
            onClick={() => setPage((value) => value - 1)}
            title="Previous page"
          >
            ←
          </button>

          {getPaginationRange(currentPage, totalPages).map((value, index) => (
            <button
              key={`${value}-${index}`}
              type="button"
              className={`page-btn ${value === currentPage ? "active" : ""}`}
              disabled={value === "..."}
              onClick={() => value !== "..." && setPage(value)}
              title={value === "..." ? "More pages" : `Go to page ${value}`}
            >
              {value === "..." ? "..." : value}
            </button>
          ))}

          <button
            type="button"
            className="page-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((value) => value + 1)}
            title="Next page"
          >
            →
          </button>
        </div>
      </div>
    </section>
  );
}
